import {Component, EventEmitter, OnInit} from '@angular/core';
import {DocumentService} from '../document.service';
import {Document} from '../document';
import {NotificationService} from '../../shared/notification.service';
import {AuthService} from "../../auth/auth.service";

@Component({
  selector: 'app-document-list',
  templateUrl: 'list.component.html',
  styleUrls: [],
})
export class DocumentListComponent implements OnInit {
  public document: Document;

  /**
   * A list of all the documents currently loaded.
   *
   * @type {Document[]}
   */
  public documents: Document[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of documents.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of documents that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of documents that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a document has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public documentDeleted: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {DocumentService} documentService
   * @param {NotificationService} notificationService
   */
  constructor(
      private documentService: DocumentService,
      private notificationService: NotificationService,
      private authService: AuthService,
  ) {
    this.documentService.loading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.documentService.totalPages.subscribe(totalPages => {
      this.totalPages = totalPages;
    });
    this.documentService.currentPage.subscribe(currentPage => {
      this.currentPage = currentPage;
    });
    this.documentService.totalResults.subscribe(totalResults => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the document list.
   */
  public ngOnInit(): void {
    this.loadDocuments(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * document list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadDocuments(pageNumber);
  }

  /**
   * Loads a list of documents using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadDocuments(page?: number, limit?: number): void {
    const listDocuments = this.documentService
        .listDocuments(page, limit)
        .subscribe((documents: Document[]) => {
          this.documents = documents;
          listDocuments.unsubscribe();
        });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate document ID
   * to delete the document.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.documentService.deleteDocument(id).subscribe(
        deleted => {
          this.notificationService.success(
              `Document Deleted`,
              `Document with ID ${id} was deleted.`,
              5,
          );
          this.documentDeleted.next(true);
          this.onPageChanged(this.currentPage);
        },
        err => {
          this.notificationService.error(
              `Document Not Deleted`,
              err.error.message,
              10,
          );
          this.documentDeleted.next(false);
          this.onPageChanged(this.currentPage);
        },
    );
  }
}
